// Use empty string to make requests relative - they'll go through Vite's proxy in dev
// or the nginx reverse proxy in production
const API_BASE_URL = '';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorCode?: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function getAuthToken(): string | null {
  return localStorage.getItem('bridge_auth_token');
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const token = getAuthToken();
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  // For POST/PUT/PATCH, always send a body (empty object if none provided)
  // Fastify requires a body when Content-Type is application/json
  const needsBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
  const requestBody = body !== undefined ? JSON.stringify(body) : (needsBody ? '{}' : undefined);

  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: requestBody,
  });

  if (!response.ok) {
    let errorMessage = `API error: ${String(response.status)} ${response.statusText}`;
    let errorCode: string | undefined;

    try {
      const errorBody = (await response.json()) as ApiError;
      errorMessage = errorBody.message || errorMessage;
      errorCode = errorBody.error;
    } catch {
      // Failed to parse error body, use default message
    }

    throw new ApiClientError(errorMessage, response.status, errorCode);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'GET', headers }),

  post: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'POST', body, headers }),

  put: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'PUT', body, headers }),

  patch: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body, headers }),

  delete: <T>(endpoint: string, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'DELETE', headers }),

  deleteWithBody: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'DELETE', body, headers }),
};

export { api as apiClient };
